import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'wouter';
import { AlertTriangle, CheckCircle2, Settings2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { getJson } from '@/lib/http';
import { photoRuleLabel } from '@/lib/photoChecksAccess';

type PromptConfig = {
  id: number;
  name: string;
  version: number;
  status: string;
  submissionType: string;
  isActive: boolean;
};

type Comparison = {
  id: number;
  status: string;
  metricsSummary: Record<string, any> | null;
  completedAt: string | null;
};

type MappingRow = {
  mediaFormatId: number;
  retailerId: number;
  onboardingStatus: string | null;
  supportLevel: string | null;
};

type Retailer = { id: number; name: string };

type AttentionItem = {
  id: string;
  title: string;
  detail: string;
  href: string;
  cta: string;
  tone: 'critical' | 'warning' | 'info';
};

function formatMappingActive(row: MappingRow): boolean {
  return row.onboardingStatus === 'ACTIVE'
    && Boolean(row.supportLevel)
    && row.supportLevel !== 'HUMAN_REVIEW_ONLY';
}

function trialSafety(summary: Record<string, any> | null | undefined): string | null {
  return summary?.overallSafetyStatus ?? null;
}

export default function AdminAIPage() {
  const [, setLocation] = useLocation();

  const postingPromptsQuery = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'POSTING'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=POSTING'),
  });
  const removalPromptsQuery = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'REMOVAL'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=REMOVAL'),
  });
  const repostPromptsQuery = useQuery({
    queryKey: ['/api/admin/ai-triage/prompts', 'REPOST'],
    queryFn: () => getJson<{ ok: true; prompts: PromptConfig[] }>('/api/admin/ai-triage/prompts?submissionType=REPOST'),
  });

  const comparisonsQuery = useQuery({
    queryKey: ['/api/admin/ai-eval/comparisons'],
    queryFn: () => getJson<{ ok: true; comparisons: Comparison[] }>('/api/admin/ai-eval/comparisons?limit=20'),
  });

  const retailersQuery = useQuery({
    queryKey: ['/api/retailers'],
    queryFn: async () => {
      const data = await getJson<any>('/api/retailers');
      if (Array.isArray(data)) return data as Retailer[];
      if (Array.isArray(data?.retailers)) return data.retailers as Retailer[];
      return [] as Retailer[];
    },
  });

  const formatEnabledQuery = useQuery({
    queryKey: ['/api/admin/ai-verification/format-mappings', 'overview-count'],
    enabled: Boolean(retailersQuery.data?.length),
    queryFn: async () => {
      const retailers = (retailersQuery.data ?? []).slice(0, 40);
      let enabled = 0;
      let total = 0;
      for (const r of retailers) {
        try {
          const res = await getJson<{ ok: true; mappings: MappingRow[] }>(
            `/api/admin/ai-verification/format-mappings?retailerId=${r.id}`,
          );
          const rows = res.mappings ?? [];
          total += rows.length;
          enabled += rows.filter(formatMappingActive).length;
        } catch {
          // skip
        }
      }
      return { enabled, total };
    },
  });

  const allPrompts = useMemo(() => ([
    ...(postingPromptsQuery.data?.prompts ?? []),
    ...(removalPromptsQuery.data?.prompts ?? []),
    ...(repostPromptsQuery.data?.prompts ?? []),
  ]), [postingPromptsQuery.data, removalPromptsQuery.data, repostPromptsQuery.data]);

  const liveRules = allPrompts.filter((p) => p.isActive && p.status === 'ACTIVE');
  const drafts = allPrompts.filter((p) => p.status === 'DRAFT');
  const latestCompleted = (comparisonsQuery.data?.comparisons ?? []).find((c) => c.status === 'completed') ?? null;
  const safety = trialSafety(latestCompleted?.metricsSummary ?? null);

  const allPromptsLoading = postingPromptsQuery.isLoading
    || removalPromptsQuery.isLoading
    || repostPromptsQuery.isLoading;

  const attention = useMemo(() => {
    if (allPromptsLoading) return [];

    const items: AttentionItem[] = [];

    if (liveRules.length === 0) {
      items.push({
        id: 'no-standards',
        title: 'No photo standards are live',
        detail: 'Photo Checks cannot help reviewers until posting (or other) standards are live.',
        href: '/admin/ai/checks',
        cta: 'Set photo standards',
        tone: 'critical',
      });
    }

    if (drafts.length > 0 && !latestCompleted) {
      items.push({
        id: 'untested-drafts',
        title: `${drafts.length} proposed change${drafts.length === 1 ? '' : 's'} not tried yet`,
        detail: 'A proposed change should be tried safely before anyone makes it live.',
        href: '/admin/ai/results',
        cta: 'Decide if it is safe',
        tone: 'warning',
      });
    }

    if (latestCompleted && safety === 'regressed') {
      items.push({
        id: 'unsafe-trial',
        title: 'Latest trial looks unsafe to make live',
        detail: 'The proposed change missed more rejected photos than the live check.',
        href: `/admin/ai/results?trial=${latestCompleted.id}`,
        cta: 'Review the decision',
        tone: 'critical',
      });
    }

    if (latestCompleted && safety === 'unknown') {
      items.push({
        id: 'inconclusive-trial',
        title: 'Latest trial is inconclusive',
        detail: 'There was not enough clean data to say the change is safer.',
        href: `/admin/ai/results?trial=${latestCompleted.id}`,
        cta: 'Review the decision',
        tone: 'warning',
      });
    }

    if (latestCompleted && safety === 'improved') {
      items.push({
        id: 'ready-live',
        title: 'A trial looks safer — decide whether to make it live',
        detail: 'The proposed change missed fewer rejected photos than the live check on the test set.',
        href: `/admin/ai/results?trial=${latestCompleted.id}`,
        cta: 'Open decision',
        tone: 'info',
      });
    }

    if (formatEnabledQuery.data && formatEnabledQuery.data.total > 0 && formatEnabledQuery.data.enabled === 0) {
      items.push({
        id: 'no-formats',
        title: 'REACTOR is not spotting any store formats yet',
        detail: 'Formats exist, but recognition is not enabled for them.',
        href: '/admin/ai/verification',
        cta: 'See how well we know our media',
        tone: 'warning',
      });
    }

    return items;
  }, [allPromptsLoading, liveRules.length, drafts.length, latestCompleted, safety, formatEnabledQuery.data]);

  const allClear = !allPromptsLoading
    && attention.length === 0
    && liveRules.length > 0;

  return (
    <div className="container mx-auto p-6 space-y-6 max-w-3xl">
      <div className="space-y-2">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">Photo Checks</p>
        <h1 className="text-2xl font-semibold">What needs my attention?</h1>
        <p className="text-sm text-muted-foreground">
          Only decisions that need you. People still make the final approve or reject on store photos.
        </p>
      </div>

      {allPromptsLoading && (
        <p className="text-sm text-muted-foreground">Checking what needs attention…</p>
      )}

      {allClear && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Nothing urgent</AlertTitle>
          <AlertDescription>
            Live standards are in place
            {liveRules.length > 0 ? ` (${liveRules.map((r) => photoRuleLabel(r.submissionType)).join(', ')})` : ''}.
            No unsafe or unfinished change trials need a decision right now.
          </AlertDescription>
        </Alert>
      )}

      {attention.length > 0 && (
        <div className="space-y-3">
          {attention.map((item) => (
            <Card
              key={item.id}
              className={
                item.tone === 'critical'
                  ? 'border-red-200'
                  : item.tone === 'warning'
                    ? 'border-amber-200'
                    : undefined
              }
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-start gap-2 text-base">
                  <AlertTriangle className={`h-4 w-4 mt-0.5 shrink-0 ${
                    item.tone === 'critical' ? 'text-red-700' : item.tone === 'warning' ? 'text-amber-700' : 'text-muted-foreground'
                  }`}
                  />
                  {item.title}
                </CardTitle>
                <CardDescription>{item.detail}</CardDescription>
              </CardHeader>
              <CardContent>
                <Button size="sm" onClick={() => setLocation(item.href)}>
                  {item.cta}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <p className="text-xs text-muted-foreground flex items-center gap-1">
        <Settings2 className="h-3.5 w-3.5" />
        Preparing trials, model settings, and queues live under Advanced.
      </p>
    </div>
  );
}
